import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Award, Shield, Heart, Leaf, ArrowRight } from 'lucide-react';

const VALUES = [
  {
    icon: Award,
    title: 'Excellence',
    description: 'We pursue perfection in every restoration, treating each item as if it were our own.',
  },
  {
    icon: Shield,
    title: 'Trust',
    description: 'Your luxury items are insured and handled with the utmost care and security.',
  },
  {
    icon: Heart,
    title: 'Passion',
    description: 'Our love for craftsmanship drives us to deliver exceptional results every time.',
  },
  {
    icon: Leaf,
    title: 'Sustainability',
    description: 'We use eco-friendly products and techniques that are gentle on your items and the planet.',
  },
];

const STATS = [
  { number: '5000+', label: 'Items Restored' },
  { number: '98%', label: 'Customer Satisfaction' },
  { number: '15+', label: 'Expert Craftsmen' },
  { number: '3', label: 'Years of Excellence' },
];

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#F5F0E6]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              About Us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] mb-6">
              The Art of Restoration
            </h1>
            <p className="font-body text-base md:text-lg text-[#66635B] max-w-2xl mx-auto">
              Where luxury meets craftsmanship. We are dedicated to preserving and restoring the items you treasure most.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInSection>
              <div className="img-hover aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1768550005921-8782adcb798c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxzaG9lJTIwY3JhZnRzbWFufGVufDB8fHx8MTc3NDY1NjA0N3ww&ixlib=rb-4.1.0&q=85"
                  alt="Expert craftsman at work"
                  className="w-full h-full object-cover"
                />
              </div>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <div>
                <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                  Our Story
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] mb-6">
                  Born from a Passion for Perfection
                </h2>
                <div className="space-y-4 font-body text-base text-[#66635B] leading-relaxed">
                  <p>
                    Drycleanspace was founded with a simple mission: to provide luxury care for luxury items. 
                    We noticed a gap in the market for truly premium cleaning and restoration services that 
                    understand the value of high-end shoes and handbags.
                  </p>
                  <p>
                    Our team comprises skilled craftsmen and restoration experts, each bringing years of 
                    experience working with the world's finest brands. From limited edition sneakers to 
                    vintage designer bags, we treat every item with the respect it deserves.
                  </p>
                  <p>
                    Today, we've restored over 5,000 items and continue to set the standard for premium 
                    care services. Our commitment to excellence has made us the trusted choice for 
                    luxury enthusiasts across the country.
                  </p>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, index) => (
              <FadeInSection key={stat.label} delay={index * 0.1}>
                <div data-testid={`stat-${index}`} className="text-center">
                  <p className="font-heading text-4xl md:text-5xl font-semibold text-[#C5A059] mb-2">
                    {stat.number}
                  </p>
                  <p className="font-body text-xs uppercase tracking-[0.15em] text-white/60">
                    {stat.label}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Our Values
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A]">
                What Sets Us Apart
              </h2>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((value, index) => (
              <FadeInSection key={value.title} delay={index * 0.1}>
                <div
                  data-testid={`value-${value.title.toLowerCase()}`}
                  className="text-center p-8 bg-[#FAFAFA] border border-[#EAE5D9] hover:border-[#C5A059] transition-colors"
                >
                  <div className="w-14 h-14 mx-auto mb-6 border border-[#C5A059] flex items-center justify-center">
                    <value.icon size={24} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-sm text-[#66635B]">
                    {value.description}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#F5F0E6]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInSection>
              <div>
                <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                  Why Choose Us
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] mb-8">
                  The Drycleanspace Difference
                </h2>
                <ul className="space-y-6">
                  {[
                    'Expert craftsmen with 10+ years of experience',
                    'Premium, eco-friendly cleaning solutions',
                    'Full insurance coverage for all items',
                    'Doorstep pickup and delivery service',
                    'Transparent pricing with no hidden costs',
                    'Satisfaction guaranteed or money back',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-6 h-6 border border-[#C5A059] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#C5A059] text-xs font-semibold">{index + 1}</span>
                      </div>
                      <span className="font-body text-base text-[#1A1A1A]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <div className="img-hover aspect-square overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1598099947145-e85739e7ca28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Luxury handbag care"
                  className="w-full h-full object-cover"
                />
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <FadeInSection>
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Get Started
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
              Ready to Experience Premium Care?
            </h2>
            <p className="font-body text-base text-white/60 mb-10 max-w-xl mx-auto">
              Join thousands of satisfied customers who trust Drycleanspace with their most treasured items.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="btn-secondary inline-flex items-center gap-2 border-white/30 text-white hover:bg-white hover:text-[#1A1A1A]"
            >
              Contact Us Today
              <ArrowRight size={16} />
            </Link>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default About;
